import {
  Box,
  Button,
  Circle,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import { continueSessionStreaming, initializeChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import { getStreamingLabel } from "utils/helperFuncUtils";
import MessageComponent from "./MessageComponent";
import { AgentInputProto, ArtifactProto, ChatClientType, chatClientTypeToJSON, ChatContext, ChatMessageProto, ChatResponse, ExecutionStepProto, ResponseMessageProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ChatMessageType, chatMessageTypeFromJSON, DarwinAgentType, DarwinHeader, ExecutionStatusType } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { AvatarWithMemo, rotateAnimation } from "components/navbar/Navbar";
import logo from "../../assets/logo_dark_small.png";
import { BsRobot } from "react-icons/bs";
import { FiDatabase, FiPlus, FiPlay } from "react-icons/fi";
import { IoAnalytics } from "react-icons/io5";
import { LuSparkles } from "react-icons/lu";
import { MdSchedule } from "react-icons/md";
import { generateRandomSuffix, getRandomWelcomeMessage } from "utils/nameUtils";
import { getAnalyst } from "services/AnalystService";
import showToast from "hooks/useCustomToast";
import { ChatRequest } from "build-ts/chat_session";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useAnalystChat } from "context/AnalystChatContext";
import { extractScenarioJobsSavedByTools } from "utils/helperFuncUtils";
import PromptButton from "./PromptButton";
import ChatAddMenuComponent from "./ChatAddMenuComponent";
import HighlightedTextarea, { type HighlightedTextareaHandle } from "./HighlightedTextarea";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { DarwinToolType } from "build-ts/darwin_agent";

interface ChatProps {
  userId?: string;
  isLoadingChat: boolean;
  chatId: string | undefined;
  setChatId: React.Dispatch<React.SetStateAction<string | undefined>>;
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sessionId?: string;
  fetchChatSessionsList?: (clientType: ChatClientType, searchQuery?: string, analyst?: Header | null) => Promise<void>;
  fetchEvaChatSessionsList?: (searchQuery?: string) => Promise<void>;
  // Artifact click handler
  onArtifactClick?: (artifact: ArtifactProto, activeTab?: string, selectedItemIndex?: number) => void;
  selectedAnalyst?: Header | null;
  // clean content function
  cleanContent?: (content: string) => string;
  // Analyst selection props
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  // Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  // All artifacts from all messages for dataframe link matching
  allArtifacts?: ArtifactProto[];
  setAllArtifacts?: React.Dispatch<React.SetStateAction<ArtifactProto[]>>;
  // Chat is saved if session header has created_date
  isChatSaved?: boolean;
  // Selected scenario ids for the menu: empty/undefined = all scenarios, otherwise multi-scenario context
  selectedScenarioIds?: string[];
  /** When set (e.g. from scenario search bar), initializes session and sends this as first message */
  initialPrompt?: string;
  /** Scenario jobs for the chat */
  scenarioJobs?: ScheduledJobProto[];
  setScenarioJobs?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
  /** Set the scenario jobs saved by tools for the chat */
  setScenarioJobsSavedByTools?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
};

export interface ChatMessage {
  id?: string;
  author: "user" | "ai";
  content: string;
  artifacts?: ArtifactProto[] | undefined;
  responseMessage?: ResponseMessageProto | undefined;
  selectedAnalystId?: string;
  ls_trace_url?: string;
  langgraph_message_json_list?: string[];
  userTimestampMs?: number;
  executionStatus?: ExecutionStatusType;
  executionSteps?: ExecutionStepProto[];
}

const Chat: React.FC<ChatProps> = ({
  userId = "",
  isLoadingChat,
  chatId,
  setChatId,
  chatMessages,
  setChatMessages,
  sessionId,
  fetchChatSessionsList,
  fetchEvaChatSessionsList,
  onArtifactClick,
  selectedAnalyst,
  isFromEva = false,
  cleanContent,
  // Analyst selection props
  analystsList = [],
  isLoadingAnalysts = false,
  onAnalystChange,
  isArtifactsPanelVisible = false,
  allArtifacts = [],
  setAllArtifacts,
  isChatSaved,
  selectedScenarioIds = [],
  initialPrompt,
  scenarioJobs,
  setScenarioJobs,
  setScenarioJobsSavedByTools,
}) => {
  const { currentSessionHeader, setCurrentSessionHeader } = useAnalystChat();
  const isInputDisabledDueToEva =
    isFromEva &&
    !!currentSessionHeader &&
    currentSessionHeader.type !== chatClientTypeToJSON(ChatClientType.EVA);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingStatusLabel, setStreamingStatusLabel] = useState("Thinking ...");
  const [inputMessage, setInputMessage] = useState<string>("");
  const [welcomeMessage] = useState(() => getRandomWelcomeMessage()); // Generate welcome message on mount
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const lastSentInitialPromptRef = useRef<string | null>(null);
  const highlightedTextareaRef = useRef<HighlightedTextareaHandle>(null);
  // Accumulates incoming streamed text tokens before we commit them to UI in batches.
  const tokenBufferRef = useRef("");
  // Tracks the currently scheduled requestAnimationFrame id for token flush batching.
  const tokenFlushRafRef = useRef<number | null>(null);
  // Becomes true when a TOOL_CALL_FAILURE step is observed; reset when next tool retry starts.
  const sawToolFailureRef = useRef(false);
  // While true, streamed apology/retry text is not rendered immediately (held as temporary text).
  const suppressRetryTextRef = useRef(false);
  // Holds suppressed retry text so it can be dropped on retry or restored if no retry follows.
  const suppressedRetryTokenBufferRef = useRef("");
  const navigate = useNavigate();

  const flushBufferedTokens = () => {
    if (tokenFlushRafRef.current !== null) {
      // cancels an animation frame request previously scheduled through a call to requestAnimationFrame()
      cancelAnimationFrame(tokenFlushRafRef.current);
      tokenFlushRafRef.current = null;
    }

    // bufferedToken is the string of tokens that have been accumulated since the last flush
    const bufferedToken = tokenBufferRef.current;
    if (!bufferedToken) return;
    tokenBufferRef.current = "";

    setChatMessages(prev => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (updated[lastIdx]?.author === "ai") {
        updated[lastIdx] = {
          ...updated[lastIdx],
          content: (updated[lastIdx].content || "") + bufferedToken,
        };
      }
      return updated;
    });
  };

  const scheduleTokenFlush = () => {
    // Keep at most one RAF callback queued at a time to avoid redundant flushes.
    if (tokenFlushRafRef.current !== null) return;
    tokenFlushRafRef.current = window.requestAnimationFrame(() => {
      // Mark queue as free before flushing so new tokens can schedule the next frame.
      tokenFlushRafRef.current = null;
      flushBufferedTokens();
    });
  };

  const appendStreamingToken = (token: string) => {
    // Ignore empty chunks to avoid pointless renders.
    if (!token) return;

    // Retry/apology chatter after a tool failure is buffered first.
    // If another tool call starts, we'll drop it as intermediate noise.
    if (suppressRetryTextRef.current) {
      suppressedRetryTokenBufferRef.current += token;
      return;
    }

    // Buffer tokens and flush in batches, rather than one render per token.
    tokenBufferRef.current += token;
    // Flush early when bursty streams produce large token batches.
    if (tokenBufferRef.current.length >= 512) {
      flushBufferedTokens();
      return;
    }
    scheduleTokenFlush();
  };

  useEffect(() => {
    return () => {
      // Drain pending token buffer on unmount.
      const bufferedToken = tokenBufferRef.current;
      if (bufferedToken) {
        // Clear the ref first so no stale content can be flushed twice.
        tokenBufferRef.current = "";
        // Best-effort final commit: preserve buffered text if unmount happens mid-stream.
        setChatMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.author === "ai") {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: (updated[lastIdx].content || "") + bufferedToken,
            };
          }
          return updated;
        });
      }
      if (tokenFlushRafRef.current !== null) {
        // Cancel any pending RAF now that component is unmounting.
        cancelAnimationFrame(tokenFlushRafRef.current);
      }
    };
  }, [setChatMessages]);

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Check if user is near bottom (within 100px)
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 50;
      // Reset when user scrolls back down near the bottom.
      userScrolledRef.current = !isNearBottom;
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current && !userScrolledRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  // Auto-scroll only when new messages added AND user hasn't manually scrolled away
  useLayoutEffect(() => {
    if (!isLoadingChat && chatMessages.length > 0) {
      scrollToBottom();
    }
  }, [chatMessages, isLoadingChat]);

  useEffect(() => {
    // Keep state updaters pure: derive saved jobs after render settles.
    // We only run this when streaming/non-streaming turn has completed.
    if (isLoading || chatMessages.length === 0 || !setScenarioJobsSavedByTools) return;
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (!lastMessage) return;
    setScenarioJobsSavedByTools(extractScenarioJobsSavedByTools([lastMessage]));
  }, [chatMessages, isLoading, setScenarioJobsSavedByTools]);

  // When initialPrompt is set (e.g. from scenario search bar), initialize session and send it once analyst is loaded
  useEffect(() => {
    if (
      !initialPrompt?.trim() ||
      chatId !== undefined ||
      isLoading ||
      lastSentInitialPromptRef.current === initialPrompt
    ) {
      return;
    }
    lastSentInitialPromptRef.current = initialPrompt;
    handleSendMessage(true, null, initialPrompt.trim());
    // Intentionally omit handleSendMessage from deps: we only want to run when analyst/prompt/chatId/isLoading change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, chatId, isLoading]);

  const handleInputChange = (value: string) => {
    setInputMessage(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (chatId || sessionId) {
      // Continue existing chat
      await handleSendMessage();
    } else {
      // New chat
      await handleSendMessage(true, selectedAnalyst);
    }
  };

  /**
   * Called for every ExecutionStep event from the stream.
   * Updates the spinner label and, if the step carries an artifact,
   * merges it into both the global artifact list and the last AI message.
   */
  const handleExecutionStep = (step: ExecutionStepProto) => {
    setStreamingStatusLabel(getStreamingLabel(step));

    // Append to executionSteps on the last AI message (for inline dataframe links).
    // Skip EXECUTING_LLM_CALL — "Thinking..." is already shown in the loader.
    const shouldRecord = step.status !== ExecutionStatusType.EXECUTING_LLM_CALL;

    if (step.status === ExecutionStatusType.TOOL_CALL_FAILURE) {
      sawToolFailureRef.current = true;
      suppressRetryTextRef.current = true;
      suppressedRetryTokenBufferRef.current = "";
    }

    // A retry started after a failure -> drop the buffered apology text.
    if (step.status === ExecutionStatusType.EXECUTING_TOOL_CALL && sawToolFailureRef.current) {
      sawToolFailureRef.current = false;
      suppressRetryTextRef.current = false;
      suppressedRetryTokenBufferRef.current = "";
      tokenBufferRef.current = "";
      if (tokenFlushRafRef.current !== null) {
        cancelAnimationFrame(tokenFlushRafRef.current);
        tokenFlushRafRef.current = null;
      }
      // reset last ai message in this case since it is failed tool call message
      // like apology/retry text
      setChatMessages(prev => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].author === "ai") {
            updated[i] = {
              ...updated[i],
              content: "",
              responseMessage: undefined,
            };
            break;
          }
        }
        return updated;
      });
    }

    if (step.status === ExecutionStatusType.ERROR_STATE) {
      setIsLoading(false);
      showToast("Something went wrong", "The response could not be completed", "error");
    }

    // Batch artifact list update + message update in one React.startTransition
    // so React can deprioritize this render and keep the input responsive
    React.startTransition(() => {
      // Update global artifact list
      if (step.artifacts.length > 0 && setAllArtifacts) {
        setAllArtifacts(prev => {
          const updated = [...prev];
          for (const artifact of step.artifacts) {
            const artifactType = artifactTypeFromJSON(artifact.artifact_type);
            if (artifactType === ArtifactType.SCENARIO_JOB && artifact.scenario_job?.header?.id) {
              const jobId = artifact.scenario_job.header.id;
              const idx = updated.findIndex(
                a =>
                  artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
                  a.scenario_job?.header?.id === jobId
              );
              if (idx >= 0) updated[idx] = artifact;
              else updated.push(artifact);
            } else {
              updated.push(artifact);
            }
          }
          return updated;
        });
      }

      // SAVING_SCENARIO is the streaming equivalent of finding a "save" tool call in
      // langgraph_message_json_list. The step carries the persisted SCENARIO_JOB artifact,
      // so we can mark it as saved-by-tools immediately — no message history parsing needed.
      const savingArtifact = step.artifacts.find(a => a.scenario_job);
      if (
        (step.status === ExecutionStatusType.SAVING_SCENARIO ||
          (step.status === ExecutionStatusType.TOOL_CALL_SUCCESS && step.tool_type === DarwinToolType.SAVE_SCENARIO_TOOL)
        ) && savingArtifact?.scenario_job
      ) {
        setScenarioJobsSavedByTools?.([savingArtifact.scenario_job]);
      }

      setChatMessages(prev => {
        const updated = [...prev];
        for (let j = updated.length - 1; j >= 0; j--) {
          if (updated[j].author === "ai") {
            const next: typeof updated[0] = { ...updated[j] };

            if (shouldRecord) {
              next.executionSteps = [...(next.executionSteps ?? []), step];
            }

            const flat = (next.executionSteps ?? []).flatMap(s => s.artifacts);
            next.artifacts = flat.length > 0 ? flat : undefined;

            updated[j] = next;
            break;
          }
        }
        return updated;
      });
    });
  };

  const applyStreamChunk = (
    chunk: ChatResponse,
    setResponse: (value: ChatResponse) => void,
  ) => {
    setResponse(chunk);

    if (!chunk.messages?.length) return;
    // using startTransition here so that setAllArtifacts and setChatMessages are batched together
    React.startTransition(() => {
      const assistantMessages = chunk.messages.filter((m) => {
        const type = chatMessageTypeFromJSON(m.message_type);
        return type === ChatMessageType.REASONING || type === ChatMessageType.RESPONSE;
      });
      if (!assistantMessages.length) return;

      // A single chunk can carry both REASONING and RESPONSE; handle both in order.
      const reasoningMsg = [...assistantMessages]
        .reverse()
        .find((m) => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.REASONING);
      const responseMsg = [...assistantMessages]
        .reverse()
        .find((m) => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.RESPONSE);

      if (reasoningMsg) {
        const validArtifacts = reasoningMsg.execution_steps.flatMap(s => s.artifacts);
        if (validArtifacts.length > 0 && setAllArtifacts) {
          setAllArtifacts(prev => {
            const updated = [...prev];
            for (const artifact of validArtifacts) {
              if (
                artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.SCENARIO_JOB &&
                artifact.scenario_job?.header?.id
              ) {
                const jobId = artifact.scenario_job.header.id;
                const existingIdx = updated.findIndex(
                  a =>
                    artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
                    a.scenario_job?.header?.id === jobId
                );
                if (existingIdx >= 0) updated[existingIdx] = artifact;
                else updated.push(artifact);
              } else {
                updated.push(artifact);
              }
            }
            return updated;
          });
        }
        setChatMessages(prev => {
          const updated = [...prev];
          // Streaming update: enrich the active AI bubble with latest reasoning metadata
          // (steps/artifacts/trace) so users can see tool progress before final text arrives.
          for (let j = updated.length - 1; j >= 0; j--) {
            if (updated[j].author === "ai") {
              updated[j] = {
                ...updated[j],
                executionSteps: reasoningMsg.execution_steps.length > 0 ? reasoningMsg.execution_steps : undefined,
                artifacts: validArtifacts.length > 0 ? validArtifacts : undefined,
                ls_trace_url: reasoningMsg.ls_trace_url,
                langgraph_message_json_list: reasoningMsg.langgraph_message_json_list?.length
                  ? reasoningMsg.langgraph_message_json_list
                  : [],
              };
              break;
            }
          }
          return updated;
        });
      }

      if (responseMsg) {
        // Response came as a JSON chunk (response-tool path, not text-streaming path).
        setChatMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.author === "ai") {
            if (responseMsg.content) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                content: responseMsg.content,
                ls_trace_url: responseMsg.ls_trace_url,
              };
            } else if (responseMsg.response_message) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                content: "",
                responseMessage: responseMsg.response_message,
                ls_trace_url: responseMsg.ls_trace_url,
              };
            }
          }
          return updated;
        });
      }
    });
  };

  const handleSendMessage = async (newChat: boolean = false, selectedAnalyst?: Header | null, messageOverride?: string) => {
    const messageToSend = (messageOverride !== undefined && messageOverride !== null ? messageOverride : inputMessage).trim();
    if (messageToSend !== "" && (chatId || newChat)) {
      const inputMsg = messageToSend;
      userScrolledRef.current = false;
      sawToolFailureRef.current = false;
      suppressRetryTextRef.current = false;
      suppressedRetryTokenBufferRef.current = "";
      const userMsg = { id: crypto.randomUUID(), content: inputMsg, author: "user" as const };
      const loadingMsg = { id: crypto.randomUUID(), content: "", author: "ai" as const };
      tokenBufferRef.current = "";

      // Add user message and loading AI message
      setChatMessages(prev => [...prev, userMsg, loadingMsg]);
      if (messageOverride === undefined) setInputMessage("");
      setIsLoading(true);

      try {
        let response: ChatResponse | undefined;
        // True when the new-chat step-2 uses streaming; controls final message processing below.
        let wasStreamed = false;
        if (newChat) {
          // New chat
          if (!selectedAnalyst) {
            showToast("Please select an analyst first", "", "warning");
            return;
          }
          const analystDetails = await getAnalyst(selectedAnalyst!.id!);
          if (!analystDetails) {
            showToast("Failed to load analyst details", "", "error");
            return;
          }

          const scenarioJobs: ScheduledJobProto[] = [];
          if (analystDetails?.header?.id) {
            const jobs = await listScheduledJobsByOwnerId(analystDetails.header.id);
            if (jobs && jobs.length > 0) {
              scenarioJobs.push(...jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
            }
          }
          if (scenarioJobs.length > 0) {
            setScenarioJobs?.(scenarioJobs);
          }
          // Create chat context (analyst chat or scenario creation)
          const envAgentType = process.env.REACT_APP_AGENT_TYPE_FOR_CHAT;
          const agentTypeFromEnv: DarwinAgentType =
            envAgentType !== undefined && envAgentType !== null && envAgentType !== ""
              ? (Number(envAgentType) as DarwinAgentType)
              : DarwinAgentType.ANALYST_CHAT_AGENT;
          const chatContext = ChatContext.create({
            agent_type: agentTypeFromEnv,
            agent_input: AgentInputProto.create({
              analyst: analystDetails,
              scenario_jobs: scenarioJobs
            })
          });

          // Step 1: Initialize the session WITHOUT a user message so we get the session ID
          // back immediately. stream_response: false ensures a fast plain-JSON reply.
          // We use this ID to navigate right away — no need to wait for the AI response.
          const initResponse = await initializeChatSession(
            ChatRequest.create({
              session_header: DarwinHeader.create({
                id: "",
                name: `Chat ${generateRandomSuffix(8)}`,
                type: chatClientTypeToJSON(ChatClientType.FRIDA),
                author_id: userId,
              }),
              chat_context: chatContext,
              is_save: true,
              stream_response: false,
            })
          );

          if (!initResponse?.session_header?.id) {
            showToast("Failed to create new chat session", "", "error");
            return;
          }

          const newSessionId = initResponse.session_header.id;
          setChatId(newSessionId);
          // Need to remove the random generated name from the session header
          const sessionHeader = { ...initResponse.session_header, name: "" };
          setCurrentSessionHeader(sessionHeader);
          navigate(`/chat/${newSessionId}`, { replace: true });

          // Shared variable for the generated title.
          let generatedTitle: string | undefined;

          // Fire the naming agent in the background — it runs independently and patches
          // the header + persists the title whenever it finishes.
          initializeChatSession(
            ChatRequest.create({
              session_header: DarwinHeader.create({
                id: "",
                type: chatClientTypeToJSON(ChatClientType.FRIDA),
                author_id: userId,
              }),
              chat_context: ChatContext.create({
                agent_type: DarwinAgentType.CHAT_NAMING_AGENT,
              }),
              message: inputMsg,
              is_save: false,
            }),
          )
            .then((resp) => {
              const titleMsg = resp?.messages?.find(
                (m) => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.RESPONSE && m.content
              );
              generatedTitle = titleMsg?.content?.trim();
              if (generatedTitle) {
                setCurrentSessionHeader((prev) =>
                  prev ? { ...prev, name: generatedTitle! } : prev
                );
                updateChatSessionHeaders([newSessionId], generatedTitle)
                  .catch((err) => console.warn("Failed to apply generated title:", err));
              }
            })
            .catch((err) => console.warn("Session naming failed (non-critical):", err));

          // Step 2: Send the actual user message using streaming.
          // Tokens arrive one-by-one and are appended to the AI message in real time.
          // ChatResponse JSON chunks carry REASONING messages (with artifacts) and are
          // processed inline as they arrive.
          wasStreamed = true;

          await continueSessionStreaming(
            newSessionId,
            inputMsg,
            isFromEva ? isChatSaved : true,
            userId,
            // onToken: each plain-text token → append to the last AI message.
            // Batched via requestAnimationFrame to avoid render storms.
            (token) => {
              appendStreamingToken(token);
            },
            // onChatResponseChunk: a full ChatResponse JSON arrived (after a tool call)
            (chunk) => {
              applyStreamChunk(chunk, value => { response = value; });
            },
            handleExecutionStep,
          );
          if (suppressRetryTextRef.current) {
            if (response?.status !== ExecutionStatusType.TOOL_CALL_FAILURE) {
              tokenBufferRef.current += suppressedRetryTokenBufferRef.current;
            }
            suppressRetryTextRef.current = false;
            suppressedRetryTokenBufferRef.current = "";
          }
          flushBufferedTokens();

          // Refresh the sidebar so the new session appears immediately.
          fetchChatSessionsList?.(ChatClientType.FRIDA);
        } else {
          // Regular mode (continuing an existing chat) — use streaming so subsequent
          // messages also appear token-by-token just like the first message.
          wasStreamed = true;

          await continueSessionStreaming(
            chatId!,
            inputMsg,
            isFromEva ? isChatSaved : true,
            userId,
            (token) => {
              appendStreamingToken(token);
            },
            (chunk) => {
              applyStreamChunk(chunk, value => { response = value; });
            },
            handleExecutionStep,
          );
          if (suppressRetryTextRef.current) {
            if (response?.status !== ExecutionStatusType.TOOL_CALL_FAILURE) {
              tokenBufferRef.current += suppressedRetryTokenBufferRef.current;
            }
            suppressRetryTextRef.current = false;
            suppressedRetryTokenBufferRef.current = "";
          }
          flushBufferedTokens();
        }

        // If response has execution status error, show the error UI and stop processing.
        if (response?.status === ExecutionStatusType.TOOL_CALL_FAILURE) {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            {
              id: crypto.randomUUID(),
              author: "ai",
              content: "",
              executionStatus: ExecutionStatusType.TOOL_CALL_FAILURE,
            },
          ]);
          return;
        }

        if (!wasStreamed) {
          // Non-streaming path: collect artifacts from the full response message list,
          // then rebuild the message array from scratch.

          // Collect new artifacts from response.
          const newArtifacts: ArtifactProto[] = [];

          if (response && response.messages) {
            // Find the index of the last HUMAN message matching the current user input,
            // so we only collect new artifacts from new reasoning messages in the *new* turn.
            const lastHumanMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
              if (
                chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN &&
                msg.content === inputMsg
              ) {
                return idx;
              }
              return lastIdx;
            }, -1);

            const reasoningMessages = response.messages.filter(
              (msg, idx) =>
                idx > lastHumanMsgIdx &&
                chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING &&
                (msg as ChatMessageProto).execution_steps.length > 0
            );
            const messagesToProcess =
              reasoningMessages.length > 0
                ? [reasoningMessages[reasoningMessages.length - 1]]
                : reasoningMessages;

            for (const msg of messagesToProcess) {
              for (const artifact of (msg as ChatMessageProto).execution_steps.flatMap(s => s.artifacts)) {
                newArtifacts.push(artifact);
              }
            }
          }

          // Add new artifacts to the cumulative list.
          // For SCENARIO_JOB: replace existing entry with same id; append everything else.
          if (newArtifacts.length > 0 && setAllArtifacts) {
            setAllArtifacts((prev) => {
              let updated = [...prev];
              for (const newArtifact of newArtifacts) {
                if (
                  artifactTypeFromJSON(newArtifact.artifact_type) === ArtifactType.SCENARIO_JOB &&
                  newArtifact.scenario_job?.header?.id
                ) {
                  const jobId = newArtifact.scenario_job.header.id;
                  const existingIdx = updated.findIndex(
                    (a) =>
                      artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
                      a.scenario_job?.header?.id === jobId
                  );
                  if (existingIdx >= 0) {
                    updated[existingIdx] = newArtifact;
                  } else {
                    updated.push(newArtifact);
                  }
                } else {
                  updated.push(newArtifact);
                }
              }
              return updated;
            });
          }

          // Remove the loading message and add the actual AI response.
          // Parse only this turn's messages to avoid attaching stale retry reasoning.
          setChatMessages(prev => {
            const updated = prev.slice(0, -1);
            if (!response || !response.messages) return updated;

            // Locate this exact user input in backend history, then only process
            // messages that came after it (i.e., current turn only).
            const userMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
              if (
                chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN &&
                msg.content === inputMsg
              ) {
                return idx;
              }
              return lastIdx;
            }, -1);
            const thisTurnMessages =
              userMsgIdx >= 0 ? response.messages.slice(userMsgIdx + 1) : response.messages;

            // Walk backward so we pick the newest RESPONSE/REASONING pair for this turn.
            let responseMsg: ChatMessageProto | undefined;
            let reasoningMsg: ChatMessageProto | undefined;
            for (let i = thisTurnMessages.length - 1; i >= 0; i--) {
              const msg = thisTurnMessages[i] as ChatMessageProto;
              const msgType = chatMessageTypeFromJSON(msg.message_type);
              if (msgType === ChatMessageType.RESPONSE && !responseMsg) {
                responseMsg = msg;
              } else if (msgType === ChatMessageType.REASONING && !reasoningMsg) {
                reasoningMsg = msg;
                if (responseMsg) break;
              } else if (msgType === ChatMessageType.HUMAN) {
                // Retry/user boundary reached: older messages belong to prior attempts.
                break;
              }
            }

            // Use backend HUMAN timestamp for the user bubble time in this turn.
            const humanMsg = thisTurnMessages.find(
              m => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.HUMAN
            ) as ChatMessageProto | undefined;
            if (humanMsg?.timestamp_ms) {
              for (let k = updated.length - 1; k >= 0; k--) {
                if (updated[k].author === "user") {
                  updated[k] = { ...updated[k], userTimestampMs: Number(humanMsg.timestamp_ms) };
                  break;
                }
              }
            }

            // No assistant response for this turn -> keep user message only.
            if (!responseMsg) return updated;

            // Attach artifacts/execution steps from latest reasoning (if present).
            const steps = reasoningMsg?.execution_steps ?? [];
            const artifactsForMessage = steps.flatMap(s => s.artifacts);
            if (responseMsg.content && responseMsg.content.length > 0) {
              // Standard text response path.
              updated.push({
                id: crypto.randomUUID(),
                author: "ai",
                content: cleanContent ? cleanContent(responseMsg.content) : responseMsg.content,
                ls_trace_url: responseMsg.ls_trace_url,
                executionSteps: steps.length > 0 ? steps : undefined,
                artifacts: artifactsForMessage.length > 0 ? artifactsForMessage : undefined,
                langgraph_message_json_list:
                  reasoningMsg?.langgraph_message_json_list?.length
                    ? reasoningMsg.langgraph_message_json_list
                    : [],
              });
            } else if (responseMsg.response_message) {
              // Structured response path (response_message payload instead of plain text).
              updated.push({
                id: crypto.randomUUID(),
                author: "ai",
                content: "",
                responseMessage: responseMsg.response_message,
                ls_trace_url: responseMsg.ls_trace_url,
                executionSteps: steps.length > 0 ? steps : undefined,
                artifacts: artifactsForMessage.length > 0 ? artifactsForMessage : undefined,
                langgraph_message_json_list:
                  reasoningMsg?.langgraph_message_json_list?.length
                    ? reasoningMsg.langgraph_message_json_list
                    : [],
              });
            }
            return updated;
          });
        } else {
          // Streaming path: content + artifacts were applied inline as chunks arrived.
          // Just apply cleanContent (if provided) and extract scenario jobs from the final state.
          setChatMessages(prev => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (updated[lastIdx]?.author === "ai") {
              // Final pass for streamed text normalization after all chunks are applied.
              if (cleanContent && updated[lastIdx].content) {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: cleanContent(updated[lastIdx].content),
                };
              }
            }
            return updated;
          });
        }

      } catch (error) {
        // Remove loading message on error
        setChatMessages(prev => prev.slice(0, -1));
        console.error("Error sending message:", error);
      } finally {
        setIsLoading(false);
        setStreamingStatusLabel("Thinking ...");
      }
    }
  };

  const hasMessages = chatMessages.length > 0;

  // Input area component - reused in both layouts
  const renderInputArea = (width: string) => (
    <Box
      w={width}
      border="1px solid"
      borderColor="gray.300"
      borderRadius="xl"
      bg="white"
      overflow="hidden"
      _focusWithin={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)" }}
      boxShadow="sm"
    >
      {!hasMessages ? (
        <>
          <HighlightedTextarea
            id="chat-input"
            ref={highlightedTextareaRef}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="How can I help?"
            minHeight="90px"
            maxHeight="150px"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
            fontSize="md"
            px={3}
            pt={3}
            pb={1}
            fontWeight="500"
          />
          <HStack justify="flex-end" spacing={2} m={2}>
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Icon as={FiPlus} boxSize={5} color="purple.600" />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg="purple.50"
                _hover={{ bg: "purple.100" }}
                aria-label="Add attachment"
                flexShrink={0}
              />
              <ChatAddMenuComponent
                sessionHeader={currentSessionHeader}
                analystId={selectedAnalyst?.id ?? ""}
                chatId={chatId ?? ""}
                scenarioJobs={scenarioJobs}
                onSelectAnalysis={(tag) => {
                  setInputMessage(prev => prev ? `${prev} ${tag}` : tag);
                  setTimeout(() => highlightedTextareaRef.current?.focus(), 300);
                }}
              />
            </Menu>
            <IconButton
              icon={isLoading ? <Spinner size="xs" color="white" /> : <Icon as={FaArrowUp} boxSize={5} color="white" />}
              onClick={handleSend}
              bg="purple.600"
              isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
              aria-label="Send Chat Message"
              borderRadius="full"
              size="md"
              _hover={{ bg: "purple.700" }}
              _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            />
          </HStack>
        </>
      ) : (
        // Not empty: Horizontal layout (textarea + button inline)
        <HStack spacing={2} align="flex-end" p={2}>
          <HighlightedTextarea
            ref={highlightedTextareaRef}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Reply..."
            minHeight="40px"
            maxHeight="150px"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
            fontSize="md"
            px={2}
            py={2}
            flex="1"
          />
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Icon as={FiPlus} boxSize={5} color="purple.600" />}
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg="purple.50"
              _hover={{ bg: "purple.100" }}
              aria-label="Add attachment"
              flexShrink={0}
              alignSelf="center"
            />
            <ChatAddMenuComponent
              sessionHeader={currentSessionHeader}
              analystId={selectedAnalyst?.id ?? ""}
              chatId={chatId ?? ""}
              scenarioJobs={scenarioJobs}
              onSelectAnalysis={(tag) => {
                setInputMessage(prev => prev ? `${prev} ${tag}` : tag);
                setTimeout(() => highlightedTextareaRef.current?.focus(), 300);
              }}
            />
          </Menu>
          <IconButton
            icon={isLoading ? <Spinner size="xs" color="white" /> : <Icon as={FaArrowUp} boxSize={4} color="white" />}
            onClick={handleSend}
            bg="purple.600"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva}
            aria-label="Send Chat Message"
            borderRadius="full"
            size="sm"
            _hover={{ bg: "purple.700" }}
            _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            flexShrink={0}
            alignSelf="center"
          />
        </HStack>
      )}
    </Box>
  );

  // Loading state
  if (isLoadingChat) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Loading...
        </Text>
      </Box>
    );
  }

  // Empty state - centered layout
  if (!hasMessages) {
    return (
      <Box position="relative" w="100%" h="100%" display="flex" flexDirection="column">
        {/* Analyst dropdown - top left (FRIDA chat only) */}
        {!isFromEva && (
          <Box position="absolute" top={4} left={4} zIndex={10}>
            {isLoadingAnalysts ? (
              <Spinner size="sm" color="purple.500" />
            ) : (
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  minW="220px"
                  maxW="500px"
                  bg="purple.50"
                  fontSize="sm"
                  fontWeight="600"
                  borderRadius="lg"
                  color="gray.700"
                  _hover={{ bg: "purple.100" }}
                  _active={{ bg: "purple.100" }}
                  rightIcon={<ChevronDownIcon />}
                  textAlign="left"
                  px={3}
                >
                  <HStack spacing={2}>
                    <Icon as={BsRobot} boxSize={6} color="purple.600" />
                    <Text isTruncated fontSize="sm" mb={0} fontWeight="600">
                      {selectedAnalyst?.name || "Select analyst"}
                    </Text>
                  </HStack>
                </MenuButton>
                <Portal>
                  <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
                    {analystsList.length === 0 ? (
                      <MenuItem isDisabled fontSize="sm" color="gray.500">
                        No analysts available
                      </MenuItem>
                    ) : (
                      analystsList.map((analyst) => (
                        <MenuItem
                          key={analyst.id}
                          onClick={() => onAnalystChange?.({ target: { value: analyst.id } } as any)}
                          fontSize="sm"
                          _hover={{ bg: "purple.50" }}
                          _focus={{ bg: "purple.50" }}
                          fontWeight="500"
                        >
                          {analyst.name}
                        </MenuItem>
                      ))
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            )}
          </Box>
        )}
        <VStack
          flex={1}
          w="100%"
          h="100%"
          justify="center"
          align="center"
          spacing={9}
          mt={-3}
        >
          <HStack spacing={5}>
            <Circle bg="purple.50" p={2}>
              <AvatarWithMemo
                src={logo}
                animation={`${rotateAnimation} 3s linear infinite`}
                name="Anko"
                size="md"
              />
            </Circle>
            <Text color="gray.700" fontSize="3xl" fontWeight="400" m={0}>
              {welcomeMessage}
            </Text>
          </HStack>
          {renderInputArea("700px")}
          {/* Prompt buttons - FRIDA chat only */}
          {!isFromEva && (
            <VStack spacing={3} align="flex-start" maxW="700px" w="100%">
              <HStack
                display={{ base: "none", md: "flex" }}
                flexShrink={0}
                color="purple.600"
                fontSize="sm"
                fontWeight="medium"
              >
                <Icon as={LuSparkles} boxSize={4} />
                <Text m={0}>Try these</Text>
              </HStack>
              <SimpleGrid columns={2} spacing={2} w="100%">
                {[
                  { text: "Explore data", icon: FiDatabase, description: "Query and explore my metrics" },
                  { text: "Start a new analysis", icon: IoAnalytics, description: "Create an analysis from scratch" },
                  { text: "Schedule an automated alert", icon: MdSchedule, description: "Set up recurring alerts" },
                  { text: "Run an existing analysis", icon: FiPlay, description: "Execute a saved analysis" },
                ].map((prompt) => (
                  <PromptButton
                    key={prompt.text}
                    icon={<Icon as={prompt.icon} boxSize={5} />}
                    title={prompt.text}
                    description={prompt.description}
                    onClick={() => {
                      handleSendMessage(!(chatId || sessionId), selectedAnalyst, prompt.description);
                    }}
                    w="100%"
                  />
                ))}
              </SimpleGrid>
            </VStack>
          )}
        </VStack>
      </Box>
    );
  }

  // With messages - bottom input layout
  return (
    <VStack w="100%" h="100%" m={0} spacing={0} mt={1}>
      <Box
        ref={messagesContainerRef}
        flex="1"
        w="100%"
        overflow="auto"
        onScroll={handleScroll}
        p={4}
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-gray-200)",
            borderRadius: "full",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "var(--chakra-colors-gray-400)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "var(--chakra-colors-gray-400) transparent",
        }}
      >
        <VStack w={isArtifactsPanelVisible ? "100%" : "80%"} mx="auto" align="stretch" spacing={0}>
          <Box flex="1" minH="0" />
          {chatMessages.map((msg, index) => (
            <MessageComponent
              key={msg.id ?? `${msg.author}-${index}`}
              author={msg.author as "user" | "ai"}
              content={msg.content}
              responseMessage={msg.responseMessage}
              artifacts={msg.artifacts}
              langgraphMessageJsonList={msg.langgraph_message_json_list}
              onArtifactClick={onArtifactClick}
              isLoading={isLoading && index === chatMessages.length - 1 && msg.author === "ai"}
              isLastMessage={index === chatMessages.length - 1}
              streamingStatusLabel={streamingStatusLabel}
              executionSteps={msg.executionSteps}
              lsTraceUrl={isFromEva ? msg.ls_trace_url : undefined}
              // Perf optimization: only the latest AI message needs full chat history context
              // during active streaming; passing chatMessages to older messages causes
              // avoidable re-renders on every token/chunk update.
              chatMessages={
                msg.author === "ai" && index === chatMessages.length - 1
                  ? chatMessages
                  : undefined
              }
              isArtifactsPanelVisible={isArtifactsPanelVisible}
              allArtifactsForLinks={allArtifacts}
              scenarioJobs={scenarioJobs}
              userTimestampMs={msg.userTimestampMs}
              executionStatus={msg.executionStatus}
            />
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>
      <Box w="100%" p={4} bg="bg.surface" display="flex" flexDirection="column" alignItems="center" mb={isArtifactsPanelVisible ? 1 : 1}>
        {renderInputArea(isArtifactsPanelVisible ? "95%" : "80%")}
        <Text fontSize="xs" color="fg.muted" mt={2} mb={0}>
          Anko is AI and may make mistakes. Please double check responses.
        </Text>
      </Box>
    </VStack>
  );
};

export default Chat;